/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { Channel } from '../models/channel';
import { ChannelPartial } from '../models/channel-partial';
import { ChannelWithRelations } from '../models/channel-with-relations';
import { Count } from '../models/count';
import { NewChannel } from '../models/new-channel';

@Injectable({
  providedIn: 'root',
})
export class ChannelControllerService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation channelControllerCreateAll
   */
  static readonly ChannelControllerCreateAllPath = '/channels/bulk';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAll$Response(params?: {
    body?: Array<NewChannel>
  }): Observable<StrictHttpResponse<Array<Channel>>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerCreateAllPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Channel>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAll(params?: {
    body?: Array<NewChannel>
  }): Observable<Array<Channel>> {

    return this.createAll$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Channel>>) => r.body as Array<Channel>)
    );
  }

  /**
   * Path part for operation channelControllerCount
   */
  static readonly ChannelControllerCountPath = '/channels/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `count()` instead.
   *
   * This method doesn't expect any request body.
   */
  count$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `count$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  count(params?: {
    where?: any;
  }): Observable<Count> {

    return this.count$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation channelControllerFindById
   */
  static readonly ChannelControllerFindByIdPath = '/channels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<ChannelWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ChannelWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById(params: {
    id: string;
    filter?: any;
  }): Observable<ChannelWithRelations> {

    return this.findById$Response(params).pipe(
      map((r: StrictHttpResponse<ChannelWithRelations>) => r.body as ChannelWithRelations)
    );
  }

  /**
   * Path part for operation channelControllerReplaceById
   */
  static readonly ChannelControllerReplaceByIdPath = '/channels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `replaceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replaceById$Response(params: {
    id: string;
    body?: Channel
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerReplaceByIdPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `replaceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replaceById(params: {
    id: string;
    body?: Channel
  }): Observable<void> {

    return this.replaceById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation channelControllerDeleteById
   */
  static readonly ChannelControllerDeleteByIdPath = '/channels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById(params: {
    id: string;
  }): Observable<void> {

    return this.deleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation channelControllerUpdateById
   */
  static readonly ChannelControllerUpdateByIdPath = '/channels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById$Response(params: {
    id: string;
    body?: ChannelPartial
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById(params: {
    id: string;
    body?: ChannelPartial
  }): Observable<void> {

    return this.updateById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation channelControllerFind
   */
  static readonly ChannelControllerFindPath = '/channels';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `find()` instead.
   *
   * This method doesn't expect any request body.
   */
  find$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<ChannelWithRelations>>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ChannelWithRelations>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `find$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  find(params?: {
    filter?: any;
  }): Observable<Array<ChannelWithRelations>> {

    return this.find$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ChannelWithRelations>>) => r.body as Array<ChannelWithRelations>)
    );
  }

  /**
   * Path part for operation channelControllerCreate
   */
  static readonly ChannelControllerCreatePath = '/channels';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `create()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create$Response(params?: {
    body?: NewChannel
  }): Observable<StrictHttpResponse<Channel>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Channel>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `create$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create(params?: {
    body?: NewChannel
  }): Observable<Channel> {

    return this.create$Response(params).pipe(
      map((r: StrictHttpResponse<Channel>) => r.body as Channel)
    );
  }

  /**
   * Path part for operation channelControllerProxyCreateAll
   */
  static readonly ChannelControllerProxyCreateAllPath = '/proxy/channels/bulk';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCreateAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreateAll$Response(params?: {
    body?: Array<NewChannel>
  }): Observable<StrictHttpResponse<Array<Channel>>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerProxyCreateAllPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Channel>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCreateAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreateAll(params?: {
    body?: Array<NewChannel>
  }): Observable<Array<Channel>> {

    return this.proxyCreateAll$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Channel>>) => r.body as Array<Channel>)
    );
  }

  /**
   * Path part for operation channelControllerProxyCount
   */
  static readonly ChannelControllerProxyCountPath = '/proxy/channels/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCount()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyCount$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerProxyCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCount$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyCount(params?: {
    where?: any;
  }): Observable<Count> {

    return this.proxyCount$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation channelControllerProxyFindById
   */
  static readonly ChannelControllerProxyFindByIdPath = '/proxy/channels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyFindById()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<ChannelWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerProxyFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ChannelWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyFindById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindById(params: {
    id: string;
    filter?: any;
  }): Observable<ChannelWithRelations> {

    return this.proxyFindById$Response(params).pipe(
      map((r: StrictHttpResponse<ChannelWithRelations>) => r.body as ChannelWithRelations)
    );
  }

  /**
   * Path part for operation channelControllerProxyReplaceById
   */
  static readonly ChannelControllerProxyReplaceByIdPath = '/proxy/channels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyReplaceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyReplaceById$Response(params: {
    id: string;
    body?: Channel
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerProxyReplaceByIdPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyReplaceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyReplaceById(params: {
    id: string;
    body?: Channel
  }): Observable<void> {

    return this.proxyReplaceById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation channelControllerProxyDeleteById
   */
  static readonly ChannelControllerProxyDeleteByIdPath = '/proxy/channels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyDeleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyDeleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerProxyDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyDeleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyDeleteById(params: {
    id: string;
  }): Observable<void> {

    return this.proxyDeleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation channelControllerProxyUpdateById
   */
  static readonly ChannelControllerProxyUpdateByIdPath = '/proxy/channels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyUpdateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateById$Response(params: {
    id: string;
    body?: ChannelPartial
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerProxyUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyUpdateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateById(params: {
    id: string;
    body?: ChannelPartial
  }): Observable<void> {

    return this.proxyUpdateById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation channelControllerProxyFind
   */
  static readonly ChannelControllerProxyFindPath = '/proxy/channels';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyFind()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFind$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<ChannelWithRelations>>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerProxyFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ChannelWithRelations>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyFind$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFind(params?: {
    filter?: any;
  }): Observable<Array<ChannelWithRelations>> {

    return this.proxyFind$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ChannelWithRelations>>) => r.body as Array<ChannelWithRelations>)
    );
  }

  /**
   * Path part for operation channelControllerProxyCreate
   */
  static readonly ChannelControllerProxyCreatePath = '/proxy/channels';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreate$Response(params?: {
    body?: NewChannel
  }): Observable<StrictHttpResponse<Channel>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelControllerService.ChannelControllerProxyCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Channel>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreate(params?: {
    body?: NewChannel
  }): Observable<Channel> {

    return this.proxyCreate$Response(params).pipe(
      map((r: StrictHttpResponse<Channel>) => r.body as Channel)
    );
  }

}
